import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, Check, X, ShieldCheck, Cpu, LineChart, Eye, Workflow } from "lucide-react";

import { useLang } from "@/context/LanguageContext";
import { MaskLine, Reveal } from "@/components/motion/Reveal";
import SectionHeading from "@/components/SectionHeading";
import BrandMarquee from "@/components/BrandMarquee";
import CTASection from "@/components/CTASection";

const HERO_IMG =
  "https://images.unsplash.com/photo-1782330300311-fbe6f4389012?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHRlY2hub2xvZ3klMjBkYXRhJTIwY2VudGVyJTIwY3lhbiUyMGxpZ2h0aW5nfGVufDB8fHx8MTc4NjE5MDg1Mnww&ixlib=rb-4.1.0&q=85";
const CASE_IMG =
  "https://images.unsplash.com/photo-1759752394755-1241472b589d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxlbnRlcnByaXNlJTIwc29mdHdhcmUlMjBkYXNoYm9hcmQlMjBhbmFseXRpY3N8ZW58MHx8fHwxNzg2MTkwODUxfDA&ixlib=rb-4.1.0&q=85";

const serviceIcons = [Cpu, LineChart, Eye, Workflow];

function Hero() {
  const { t } = useLang();
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, 200]);

  return (
    <section
      ref={ref}
      data-testid="hero"
      className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden"
    >
      <motion.div
        style={{ y: yGlow }}
        className="absolute -top-40 -right-40 w-[700px] h-[700px] pointer-events-none"
      >
        <div
          className="w-full h-full"
          style={{
            background: "radial-gradient(circle, rgba(0,229,255,0.16), transparent 60%)",
          }}
        />
      </motion.div>

      {/* grid lines */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center w-full">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-10 bg-cyan-precision" />
            <span className="font-mono-tech text-[12px] uppercase tracking-widest text-cyan-precision">
              {t.home.heroEyebrow}
            </span>
          </motion.div>

          <h1 className="font-display font-light tracking-tighter text-[clamp(2.75rem,7vw,5.5rem)] leading-[0.98] text-[#F0F4F8]">
            <MaskLine delay={0.15}>{t.home.heroLine1}</MaskLine>
            <MaskLine delay={0.32} className="text-gradient-cyan">
              {t.home.heroLine2}
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-8 text-lg text-[#94A3B8] leading-relaxed max-w-xl"
          >
            {t.home.heroSub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => navigate("/contact")}
              className="inline-flex items-center gap-2 font-mono-tech text-[13px] uppercase tracking-widest bg-cyan-precision text-navy px-7 py-4 hover:bg-cyan-bright transition-colors cyan-glow"
            >
              {t.home.heroCta} <ArrowUpRight size={16} />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => navigate("/solutions")}
              className="inline-flex items-center gap-2 font-mono-tech text-[13px] uppercase tracking-widest border border-white/20 text-[#F0F4F8] px-7 py-4 hover:border-cyan-precision hover:text-cyan-precision transition-colors"
            >
              {t.home.heroCta2} <ArrowRight size={16} />
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            style={{ y: yImg }}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div
              className="relative overflow-hidden border border-cyan-precision/30"
              style={{ clipPath: "polygon(0 0, 100% 0, 100% 88%, 90% 100%, 0 100%)" }}
            >
              <img
                src={HERO_IMG}
                alt="Abstract AI circuit"
                className="w-full h-[420px] lg:h-[520px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/20 to-transparent" />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-navy border border-white/10 px-5 py-4 backdrop-blur-sm">
              <div className="font-mono-tech text-[11px] uppercase tracking-widest text-cyan-precision">
                Kecerdasan Terukur
              </div>
              <div className="font-display text-sm text-[#F0F4F8]/70 mt-1">Solusi Terstruktur</div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* stats bar */}
      <div className="absolute bottom-0 inset-x-0 border-t border-white/10 bg-navy/40 backdrop-blur-sm hidden md:block">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 grid grid-cols-3 divide-x divide-white/10">
          {[
            { v: "40+", l: t.home.statClients },
            { v: "99.9%", l: t.home.statUptime },
            { v: "3.4x", l: t.home.statRoi },
          ].map((s, i) => (
            <div key={i} className="py-6 px-4 first:pl-0">
              <div className="font-display text-3xl font-light text-cyan-precision">{s.v}</div>
              <div className="font-mono-tech text-[11px] uppercase tracking-widest text-[#94A3B8] mt-1">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ValueProp() {
  const { t } = useLang();
  return (
    <section data-testid="value-prop" className="py-28 lg:py-36">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
        <SectionHeading eyebrow={t.home.valueEyebrow} title={t.home.valueTitle} sub={t.home.valueBody} />
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full border border-white/10 bg-navy-surface/40 p-8 lg:p-10">
              <div className="font-mono-tech text-[12px] uppercase tracking-widest text-[#94A3B8] mb-8">
                {t.home.genericTitle}
              </div>
              <ul className="space-y-5">
                {t.home.generic.map((g, i) => (
                  <li key={i} className="flex items-start gap-4 text-[#94A3B8]">
                    <X size={18} className="mt-1 shrink-0 text-red-400/70" />
                    <span className="leading-relaxed">{g}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full border border-cyan-precision/40 bg-navy-surface p-8 lg:p-10 cyan-glow">
              <div className="font-mono-tech text-[12px] uppercase tracking-widest text-cyan-precision mb-8">
                {t.home.customTitle}
              </div>
              <ul className="space-y-5">
                {t.home.custom.map((c, i) => (
                  <li key={i} className="flex items-start gap-4 text-[#F0F4F8]">
                    <Check size={18} className="mt-1 shrink-0 text-cyan-precision" />
                    <span className="leading-relaxed">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function CoreServices() {
  const { t } = useLang();
  return (
    <section data-testid="core-services" className="py-28 lg:py-36 border-t border-white/10">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
        <SectionHeading eyebrow={t.home.servicesEyebrow} title={t.home.servicesTitle} />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6">
          {t.home.services.map((s, i) => {
            const Icon = serviceIcons[i];
            const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7"];
            return (
              <Reveal key={i} delay={i * 0.08} className={spans[i]}>
                <div className="group h-full border border-white/10 bg-navy-surface/40 p-8 lg:p-10 transition-all duration-300 hover:-translate-y-1 hover:border-cyan-precision/50">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 border border-white/10 flex items-center justify-center text-cyan-precision group-hover:border-cyan-precision/50 transition-colors">
                      <Icon size={22} strokeWidth={1.5} />
                    </div>
                    <span className="font-mono-tech text-[11px] uppercase tracking-widest text-[#94A3B8]">
                      {s.tag}
                    </span>
                  </div>
                  <h3 className="mt-8 font-display text-2xl font-light tracking-tight text-[#F0F4F8]">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-[#94A3B8] leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Security() {
  const { t } = useLang();
  return (
    <section data-testid="security" className="py-28 lg:py-36 border-t border-white/10">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionHeading
            eyebrow={t.home.securityEyebrow}
            title={t.home.securityTitle}
            sub={t.home.securityBody}
          />
          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-wrap gap-3">
              {t.home.badges.map((b, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-widest border border-white/15 text-[#F0F4F8]/80 px-4 py-2.5 rounded-full"
                >
                  <ShieldCheck size={13} className="text-cyan-precision" />
                  {b}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <div className="border border-white/10 bg-navy-surface/40">
              <div className="px-6 py-4 border-b border-white/10 font-mono-tech text-[11px] uppercase tracking-widest text-[#94A3B8]">
                {t.home.stackTitle}
              </div>
              <ul>
                {t.home.stack.map((s, i) => (
                  <li
                    key={i}
                    className="group flex items-center justify-between px-6 py-6 border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="flex items-center gap-5">
                      <span className="font-mono-tech text-xs text-cyan-precision/70">
                        0{i + 1}
                      </span>
                      <span className="font-display text-lg text-[#F0F4F8]">{s.n}</span>
                    </div>
                    <span className="text-sm text-[#94A3B8] text-right max-w-[45%]">{s.d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function CaseStudies() {
  const { t } = useLang();
  return (
    <section data-testid="case-studies" className="py-28 lg:py-36 border-t border-white/10">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <SectionHeading eyebrow={t.home.caseEyebrow} title={t.home.caseTitle} />
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="overflow-hidden border border-white/10">
                <img src={CASE_IMG} alt="Analytics dashboard" className="w-full h-56 object-cover" />
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {t.home.cases.map((c, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="group h-full border border-white/10 bg-navy-surface/40 p-8 transition-all duration-300 hover:-translate-y-1 hover:border-cyan-precision/50">
                <span className="font-mono-tech text-[11px] uppercase tracking-widest text-[#94A3B8]">
                  {c.sector}
                </span>
                <div className="mt-6 font-display text-6xl font-light text-cyan-precision tracking-tighter">
                  {c.metric}
                </div>
                <div className="mt-2 font-display text-lg text-[#F0F4F8]">{c.label}</div>
                <p className="mt-4 text-sm text-[#94A3B8] leading-relaxed">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-8 font-mono-tech text-[11px] text-[#94A3B8]/60">{t.home.caseNote}</p>
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useLang();
  return (
    <div data-testid="home-page">
      <Hero />
      <BrandMarquee />
      <ValueProp />
      <CoreServices />
      <Security />
      <CaseStudies />
      <CTASection
        eyebrow={t.home.ctaEyebrow}
        title={t.home.ctaTitle}
        body={t.home.ctaBody}
        btn={t.home.ctaBtn}
      />
    </div>
  );
}
